import React, { useMemo, FunctionComponent } from 'react';
import { getReasonPhrase } from '@rbx/core';
import { Grid, Typography, Accordion, AccordionSummary, AccordionDetails } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import ContentTable from '@modules/ui/shared/components/ContentTable';
import OpenCloudAPIResponse from '../../../utils/interfaces/OpenCloudAPIResponse';
import useReturnsContainerStyles from './ReturnsContainer.styles';
import ResponseTableIterator from './ResponseTableIterator';

/**
 * A comparator function to sort an array of @see {OpenCloudAPIResponse} object
 * by @see {OpenCloudAPIResponse.code} - the status code of the response
 *
 * Note outside of typical Http status code, it can also be 'default' to
 * indicates a "catch-all" default response, and when that happens 'default'
 * should be always be the first element in the array for later rendering
 *
 * @param leftResponse left side of the @see {OpenCloudAPIResponse} object to compare
 * @param rightResponse right side of the @see {OpenCloudAPIResponse} object to compare
 * @returns -1, 0, 1 to indicates how two @see {OpenCloudAPIResponse} object
 * should be rearranged during sort
 */
const responseComparator = (
  leftResponse: OpenCloudAPIResponse,
  rightResponse: OpenCloudAPIResponse,
) => {
  const { code: leftCode } = leftResponse;
  const { code: rightCode } = rightResponse;

  if (leftCode === 'default' || leftCode < rightCode) {
    return -1;
  }

  if (rightCode === 'default' || leftCode > rightCode) {
    return 1;
  }

  return 0;
};

interface ReturnsContainerProps {
  responses: OpenCloudAPIResponse[];
}

const ReturnsContainer: FunctionComponent<React.PropsWithChildren<ReturnsContainerProps>> = ({
  responses,
}) => {
  const { translate } = useTranslation();

  // From the OAS spec: The Responses Object MUST contain at least one response
  // code, and it SHOULD be the response for a successful operation call.
  const sortedResponses = useMemo(() => responses.sort(responseComparator), [responses]);

  const successResponse = useMemo(() => {
    const response200 = responses.find((r) => r.code === '200');
    if (!response200) return null;
    const { response } = response200;
    const { content, description } = response;
    if (!content) return { description };
    const contentKeys = Object.keys(content);
    if (contentKeys.length === 0) return null;
    if (contentKeys.length > 1 && process.env.environment !== 'production')
      // eslint-disable-next-line no-console
      console.warn('content keys more than 1', contentKeys);
    const contentKey = contentKeys[0];
    const contentValue = content[contentKey];
    const { schema } = contentValue;
    const { example } = schema;
    return { contentType: contentKey, example, description, response, content: contentValue };
  }, [responses]);

  const otherSuccessResponses = useMemo(() => {
    return responses.filter((r) => r.code !== '200' && r.code.startsWith('2'));
  }, [responses]);

  const otherResponses = useMemo(() => {
    return responses.filter((r) => !r.code.startsWith('2'));
  }, [responses]);

  const {
    classes: { bold, returnsHeading, accordionContainer },
  } = useReturnsContainerStyles();

  if (sortedResponses.length === 0) {
    throw new Error(`The 'responses' object must contain at least one response code!`);
  }

  if (
    !successResponse?.content &&
    !successResponse?.description &&
    otherSuccessResponses.length === 0 &&
    otherResponses.length === 0
  ) {
    return null;
  }

  return (
    <Grid container direction='column'>
      <Grid className={returnsHeading} item XSmall>
        <Typography variant='h4' component='h4' display='inline'>
          {translate('Label.Response')}
        </Typography>
      </Grid>
      <Grid container direction='column' wrap='nowrap' className={accordionContainer}>
        {successResponse?.content && (
          <Grid item XSmall>
            <Accordion defaultExpanded TransitionProps={{ mountOnEnter: true }}>
              <AccordionSummary>
                <Typography variant='largeLabel1'>{`${200} - ${getReasonPhrase(200)}`}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ResponseTableIterator
                  mediaTypeObject={successResponse.content}
                  contentType={successResponse.contentType}
                  description={successResponse.description}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
        {otherSuccessResponses.map((response) => (
          <Grid item XSmall key={response.code}>
            <Accordion TransitionProps={{ mountOnEnter: true }}>
              <AccordionSummary>
                <Typography variant='largeLabel1'>
                  {`${response.code} - ${getReasonPhrase(response.code)}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant='body1'>{response.response?.description}</Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
        {otherResponses.length > 0 && (
          <Grid item XSmall>
            <Accordion TransitionProps={{ mountOnEnter: true }}>
              <AccordionSummary>
                <Typography variant='largeLabel1'>{translate('Label.OtherCodes')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ContentTable
                  data={otherResponses}
                  renderer={(row) => {
                    const { code, description } = row as { code: string; description: string };
                    return {
                      name: (
                        <Typography className={bold} variant='body2'>{`${code} - ${getReasonPhrase(
                          code,
                        )}`}</Typography>
                      ),
                      summary: description,
                    };
                  }}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default ReturnsContainer;
